/*
 * factory-kit.js: shared helpers + custom elements for the store factory blocks (Horizon 4.2+).
 *
 * - Cart mutations go through the Ajax Cart API and announce themselves with the standard
 *   @shopify/events classes, exactly like Horizon's own product form, so the native drawer,
 *   header bubble, fly-to-cart and sticky bar all update without knowing about these blocks.
 * - No colors or fonts here: blocks read --factory-* custom properties (see README files).
 * - Loaded as a module by each block that needs it; the browser runs it once.
 */
import { CartLinesUpdateEvent, CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = () => (window.Theme && window.Theme.routes) || {};
const withJs = (u) => (u.endsWith('.js') ? u : `${u}.js`);
const URLS = {
  add: () => withJs(routes().cart_add_url || '/cart/add'),
  change: () => withJs(routes().cart_change_url || '/cart/change'),
  update: () => withJs(routes().cart_update_url || '/cart/update'),
  cart: () => withJs(routes().cart_url || '/cart'),
};

export const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Section ids of every cart-items-component on the page (drawer + /cart), like Horizon collects them. */
const cartSectionIds = () =>
  Array.from(
    new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId)
    )
  );

function deferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(() => {});
  return { promise, resolve, reject };
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || 'Cart request failed');
    error.data = data;
    throw error;
  }
  return data;
}

export async function getCart() {
  const response = await fetch(URLS.cart(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * Adds one or more variants. items: [{ id: variantId, quantity, properties? }]
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export async function addToCart(items, source = document, context = 'product') {
  const sections = cartSectionIds();
  const pending = deferred();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: Number(item.quantity) || 1 })),
      promise: pending.promise,
    })
  );
  try {
    const added = await postJSON(URLS.add(), { items, sections, sections_url: window.location.pathname });
    const cart = await getCart();
    pending.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: added.sections, source: 'factory-kit', itemCount: cart.item_count, didError: false },
    });
    return { ok: true };
  } catch (error) {
    const cart = await getCart().catch(() => null);
    pending.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: { didError: true, source: 'factory-kit', items: cart ? cart.items : [] },
    });
    source.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    return { ok: false, message: error.message };
  }
}

/** Changes a line by key (0 removes it). */
export async function changeLine(key, quantity, source = document) {
  const sections = cartSectionIds();
  const pending = deferred();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity === 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: pending.promise,
    })
  );
  try {
    const cart = await postJSON(URLS.change(), { id: key, quantity, sections, sections_url: window.location.pathname });
    pending.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: cart.sections, source: 'factory-kit', itemCount: cart.item_count, didError: false },
    });
    return { ok: true };
  } catch (error) {
    pending.resolve({ cart: null, detail: { didError: true, source: 'factory-kit' } });
    source.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    return { ok: false, message: error.message };
  }
}

/**
 * Adds a discount code next to the codes already on the cart (Ajax `discount` on /cart/update.js).
 * The code is stored on the cart even while the cart is empty; Shopify marks it applicable once
 * the cart qualifies. Returns { ok, applicable }.
 */
export async function applyDiscount(code, source = document) {
  const clean = String(code || '').trim();
  if (!clean) return { ok: false, applicable: false };
  const current = await getCart().catch(() => ({ discount_codes: [] }));
  const codes = (current.discount_codes || []).map((entry) => entry.code);
  if (!codes.some((c) => c.toLowerCase() === clean.toLowerCase())) codes.push(clean);
  const sections = cartSectionIds();
  const pending = deferred();
  source.dispatchEvent(
    new CartDiscountUpdateEvent({ discountCodes: codes.map((c) => ({ code: c })), promise: pending.promise })
  );
  try {
    const cart = await postJSON(URLS.update(), { discount: codes.join(','), sections, sections_url: window.location.pathname });
    pending.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: cart.sections } });
    const match = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === clean.toLowerCase());
    return { ok: Boolean(match), applicable: Boolean(match && match.applicable) };
  } catch (error) {
    pending.resolve({ cart: null, detail: { didError: true } });
    return { ok: false, applicable: false };
  }
}

/* ---------------------------------------------------------------- delivery estimate */

const DAY = 86400000;
/** Y-M-D and hour "now" in the warehouse time zone. */
function nowIn(timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return { date: Date.UTC(get('year'), get('month') - 1, get('day')), hour: get('hour'), minute: get('minute') };
  } catch (e) {
    const d = new Date();
    return { date: Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()), hour: d.getHours(), minute: d.getMinutes() };
  }
}
const iso = (t) => new Date(t).toISOString().slice(0, 10);

/**
 * Business-day estimate: order before the cutoff (warehouse time) and day 0 is today,
 * otherwise the next business day; weekends and listed holidays are skipped.
 */
export function estimateDelivery({ min, max, cutoff = 24, timeZone, holidays = [], weekend = [0, 6] }) {
  const now = nowIn(timeZone);
  const isBusiness = (t) => !weekend.includes(new Date(t).getUTCDay()) && !holidays.includes(iso(t));
  let start = now.date;
  if (!isBusiness(start) || now.hour >= cutoff) {
    do start += DAY; while (!isBusiness(start));
  }
  const addBusiness = (from, n) => {
    let t = from;
    let k = 0;
    while (k < n) {
      t += DAY;
      if (isBusiness(t)) k += 1;
    }
    return t;
  };
  const shipsToday = start === now.date;
  const minutesLeft = shipsToday && cutoff < 24 ? (cutoff - now.hour) * 60 - now.minute : null;
  return { start: addBusiness(start, min), end: addBusiness(start, max), shipsToday, minutesLeft };
}

class FactoryDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const d = this.dataset;
    const min = Number(d.min);
    const max = Number(d.max || d.min);
    if (!min || !this.querySelector('[data-factory-eta]')) return;
    const result = estimateDelivery({
      min,
      max,
      cutoff: d.cutoff ? Number(d.cutoff) : 24,
      timeZone: d.timezone || undefined,
      holidays: (d.holidays || '').split(',').map((s) => s.trim()).filter(Boolean),
    });
    const locale = document.documentElement.lang || 'en';
    const fmtOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
    if (d.weekday !== undefined) fmtOptions.weekday = 'short';
    const fmt = new Intl.DateTimeFormat(locale, fmtOptions);
    const start = fmt.format(result.start);
    const end = fmt.format(result.end);
    const range = start === end ? start : (d.rangeTemplate || '{start} to {end}').replace('{start}', start).replace('{end}', end);
    this.querySelectorAll('[data-factory-eta]').forEach((el) => {
      el.textContent = range;
    });
    const cd = this.querySelector('[data-factory-countdown]');
    if (cd && result.minutesLeft && result.minutesLeft > 0 && result.minutesLeft < 12 * 60) {
      const h = Math.floor(result.minutesLeft / 60);
      const m = result.minutesLeft % 60;
      cd.textContent = (d.countdownTemplate || '').replace('{time}', h ? `${h} h ${m} min` : `${m} min`);
      cd.hidden = false;
    }
    this.toggleAttribute('data-ready', true);
  }
}
if (!customElements.get('factory-delivery-estimate')) customElements.define('factory-delivery-estimate', FactoryDeliveryEstimate);

/* ---------------------------------------------------------------- add button */

/** <factory-add-button data-variant-id="123" data-quantity="1"><button>Add</button></factory-add-button>
 *  The listener sits on the element itself, so it survives Horizon morphing the drawer markup. */
class FactoryAddButton extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (this.hasAttribute('aria-busy')) return;
      const id = this.dataset.variantId;
      if (!id) return;
      this.setAttribute('aria-busy', 'true');
      const result = await addToCart([{ id: Number(id), quantity: Number(this.dataset.quantity) || 1 }], this, this.dataset.context || 'product');
      this.removeAttribute('aria-busy');
      const note = this.querySelector('[data-factory-error]');
      if (note) {
        note.textContent = result.ok ? '' : result.message || '';
        note.hidden = result.ok;
      }
    });
  }
}
if (!customElements.get('factory-add-button')) customElements.define('factory-add-button', FactoryAddButton);

/* ---------------------------------------------------------------- cart upsell */

/**
 * Loads complementary products (Search & Discovery) through the Section Rendering API into the drawer.
 * Server-rendered fallback rows stay when Shopify returns nothing.
 */
class FactoryCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id'];

  attributeChangedCallback() {
    if (this.isConnected) this.connectedCallback();
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId || 'factory-cart-upsell';
    if (!productId || this.dataset.loaded === productId) return;
    this.dataset.loaded = productId;
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${base}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=${this.dataset.limit || 3}&intent=complementary&section_id=${sectionId}`;
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
        const rows = Array.from(doc.querySelectorAll('[data-factory-upsell-row]')).filter(
          (row) => !exclude.includes(row.dataset.productId)
        );
        if (!rows.length) return;
        const list = this.querySelector('[data-factory-upsell-list]');
        if (!list) return;
        list.replaceChildren(...rows.slice(0, Number(this.dataset.limit) || 3));
        this.hidden = false;
      })
      .catch(() => {});
  }
}
if (!customElements.get('factory-cart-upsell')) customElements.define('factory-cart-upsell', FactoryCartUpsell);

/* ---------------------------------------------------------------- gift wrap */

/** Checkbox that keeps exactly one gift-wrap line in the cart (delegated, morph-safe). */
class FactoryGiftWrap extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('change', async (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
      input.disabled = true;
      const result = input.checked
        ? await addToCart([{ id: Number(this.dataset.variantId), quantity: 1 }], this, 'cart')
        : await changeLine(this.dataset.lineKey, 0, this);
      if (!result.ok) input.checked = !input.checked;
      input.disabled = false;
    });
  }
}
if (!customElements.get('factory-gift-wrap')) customElements.define('factory-gift-wrap', FactoryGiftWrap);

/** Free gift wrap without a product: a cart attribute the team sees on the order. */
class FactoryGiftNote extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const save = (attributes) =>
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      }).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
      });
    const box = this.querySelector('.factory-gift__message');
    const messageName = this.dataset.messageAttribute;
    this.addEventListener('change', async (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
      input.disabled = true;
      const name = this.dataset.attribute || 'Gift wrap';
      const attributes = { [name]: input.checked ? 'Yes' : '' };
      // unticking gift wrap clears the card message too, so no stray note reaches the order
      if (messageName && !input.checked) attributes[messageName] = '';
      try {
        await save(attributes);
        if (box) {
          box.hidden = !input.checked;
          if (!input.checked) box.querySelector('textarea').value = '';
        }
      } catch (e) {
        input.checked = !input.checked;
      }
      input.disabled = false;
    });
    // card message: saved as you type (debounced) and on leaving the field
    const area = box && box.querySelector('textarea');
    if (area && messageName) {
      const status = box.querySelector('.factory-gift__message-status');
      let timer;
      const flush = async () => {
        clearTimeout(timer);
        try {
          await save({ [messageName]: area.value.trim() });
          if (status) status.textContent = '';
        } catch (e) {
          if (status) status.textContent = '!';
        }
      };
      area.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(flush, 700);
      });
      area.addEventListener('blur', flush);
    }
  }
}
if (!customElements.get('factory-gift-note')) customElements.define('factory-gift-note', FactoryGiftNote);

/* ---------------------------------------------------------------- promo plate */

class FactoryPromoApply extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('button[data-factory-apply]');
      if (!button || this.hasAttribute('data-saved') || button.hasAttribute('aria-busy')) return;
      button.setAttribute('aria-busy', 'true');
      const result = await applyDiscount(this.dataset.code, this);
      button.removeAttribute('aria-busy');
      const note = this.querySelector('[data-factory-note]');
      if (result.ok) {
        this.setAttribute('data-saved', '');
        button.textContent = this.dataset.savedLabel || button.textContent;
        if (note && this.dataset.savedNote) note.textContent = this.dataset.savedNote;
      } else if (note && this.dataset.errorNote) {
        note.textContent = this.dataset.errorNote;
      }
    });
  }
}
if (!customElements.get('factory-promo-apply')) customElements.define('factory-promo-apply', FactoryPromoApply);

/* ---------------------------------------------------------------- quantity tiers */

/**
 * Radio tiers (1 / 2 / 3 ...) that drive Horizon's quantity selector in the same section.
 * The price shown per tier comes from Liquid; after a variant change the block is re-read
 * from the section HTML that Horizon already fetched.
 */
class FactoryQtyTiers extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.#onChange);
    this.#section?.addEventListener('shopify:product:select', this.#onProductSelect);
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener('shopify:product:select', this.#onProductSelect);
  }

  get quantity() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked ? Number(checked.value) : 1;
  }

  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.#apply();
  };

  #apply() {
    const qty = this.quantity;
    const scope = this.#section || document;
    const selector = scope.querySelector('product-form-component quantity-selector-component');
    const input = scope.querySelector('product-form-component input[name="quantity"]');
    if (selector && typeof selector.setValue === 'function') selector.setValue(String(qty));
    if (input) input.value = String(qty);
    const target = selector || input || this;
    import('@theme/events')
      .then(({ QuantitySelectorUpdateEvent }) => target.dispatchEvent(new QuantitySelectorUpdateEvent(qty)))
      .catch(() => {});
  }

  #onProductSelect = (event) => {
    if (!event.promise) return;
    event.promise
      .then(({ detail }) => {
        const html = detail && detail.html;
        if (!html || !html.querySelector) return;
        const fresh = html.querySelector(`factory-qty-tiers[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;
        const keep = this.quantity;
        this.innerHTML = fresh.innerHTML;
        const radio = this.querySelector(`input[type="radio"][value="${keep}"]`);
        if (radio) radio.checked = true;
        this.#apply();
      })
      .catch(() => {});
  };
}
if (!customElements.get('factory-qty-tiers')) customElements.define('factory-qty-tiers', FactoryQtyTiers);
